import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, IconButton, MainTitle, StickyBottomWrapper, useDialog } from '@repo/ui';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';

import type { checkNeedPaymentCardQuery as CheckNeedPaymentCardQuery } from '@/__generated__/checkNeedPaymentCardQuery.graphql';
import type { saveApplyRefundDelegationFilesMutation } from '@/__generated__/saveApplyRefundDelegationFilesMutation.graphql';
import { useLookupResultRefundData } from '@/components/tax-refund/lookup/result/LookupResultContext.tsx';
import { PoaPreview } from '@/components/tax-refund/power-of-attorney/PoaPreview.tsx';
import { saveApplyRefundDelegationFiles as saveApplyRefundDelegationFilesQuery } from '@/graphql/mutation/taxRefund/saveApplyRefundDelegationFiles.ts';
import { checkNeedPaymentCardQuery } from '@/graphql/query/paymentCard/checkNeedPaymentCard.ts';
import { COMMON_PATHS } from '@/lib/constants/paths.ts';
import { A4_HEIGHT, A4_WIDTH } from '@/lib/constants/pdf.ts';
import { usePowerOfAttorneyDocumentHtml } from '@/lib/hooks/use-power-of-attorney-document-html.ts';
import { poaFormValuesAtom, poaSaveResultAtom } from '@/lib/stores/power-of-attorney.ts';
import { BusinessType } from '@/lib/types/request.ts';
import { blobToBase64, dataUrlToBlob, generatePdf } from '@/lib/utils/file.ts';
import { handleErrorCode } from '@/lib/utils/handleErrorCode.ts';

export const PoaSubmit = ({ businessType }: { businessType: BusinessType }) => {
  const router = useRouter();
  const formValues = useAtomValue(poaFormValuesAtom);
  const setPoaSaveResult = useSetAtom(poaSaveResultAtom);
  const { documentHtml } = usePowerOfAttorneyDocumentHtml(formValues);
  const { showDialog } = useDialog();
  const { refundId } = useLookupResultRefundData();
  const captureRef = useRef<HTMLDivElement | null>(null);
  const [isPreparingDocument, setIsPreparingDocument] = useState(false);
  const { sendClickEvent } = useUserEventLogger();
  const [commitSaveApplyRefundDelegationFiles, isSaveApplyRefundDelegationFilesInFlight] =
    useMutation<saveApplyRefundDelegationFilesMutation>(saveApplyRefundDelegationFilesQuery);

  const checkNeedCardData = useLazyLoadQuery<CheckNeedPaymentCardQuery>(checkNeedPaymentCardQuery, { refundId }, { fetchPolicy: 'network-only' });

  const moveNextPage = async () => {
    await router.push({
      pathname: checkNeedCardData?.checkNeedPaymentCard?.result ? COMMON_PATHS.환급결제카드등록 : COMMON_PATHS.환급계좌입력,
      query: {
        type: businessType,
        fromLookup: true
      }
    });
  };

  const requestSaveApplyRefundDelegationFiles = async (variables: {
    signedAt: string;
    autographImage: string;
    delegationDocumentFile: string;
    refundId: string;
  }): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      commitSaveApplyRefundDelegationFiles({
        variables,
        onCompleted: (response, errors) => {
          const saveResult = response?.saveApplyRefundDelegationFiles;

          if (errors && errors.length > 0) {
            reject(new Error(errors[0]?.message ?? '위임장 저장 중 오류가 발생했습니다.'));
            return;
          }

          if (saveResult?.errors?.code) {
            void router.push(handleErrorCode(businessType, saveResult.errors.code));
            resolve(false);
            return;
          }

          if (saveResult?.result) {
            setPoaSaveResult({
              autographImageKey: saveResult.autographImageKey as string,
              delegationFileKey: saveResult.delegationFileKey as string
            });
            resolve(true);
            return;
          }

          reject(new Error('위임장 저장에 실패했습니다.'));
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  };

  const saveDocument = async () => {
    if (isPreparingDocument || isSaveApplyRefundDelegationFilesInFlight) {
      return;
    }

    if (!captureRef.current) {
      showDialog({
        title: '위임장 저장 준비 중이에요',
        description: '문서를 준비하는 중이에요. 잠시 후 다시 시도해주세요.'
      });
      return;
    }

    if (!formValues.autographDataUrl || !formValues.signedAt) {
      showDialog({
        title: '서명 정보가 필요해요',
        description: '서명과 서명 시간이 모두 필요해요. 다시 서명 후 시도해주세요.'
      });
      return;
    }

    sendClickEvent('request_after_autograph_next');
    setIsPreparingDocument(true);

    try {
      const [autographImageBolb, delegationDocumentFileBlob] = await Promise.all([
        dataUrlToBlob(formValues.autographDataUrl),
        generatePdf({
          targetElement: captureRef.current,
          fileName: `delegation-${refundId}.pdf`
        })
      ]);

      const [autographImage, delegationDocumentFile] = await Promise.all([blobToBase64(autographImageBolb), blobToBase64(delegationDocumentFileBlob)]);
      const shouldMoveNextPage = await requestSaveApplyRefundDelegationFiles({
        signedAt: formValues.signedAt,
        autographImage,
        delegationDocumentFile,
        refundId
      });

      if (!shouldMoveNextPage) {
        return;
      }

      await moveNextPage();
    } catch (error) {
      showDialog({
        title: '위임장 저장을 실패했어요',
        description: '잠시 후 다시 시도해주세요.'
      });
      if (error instanceof Error) console.error('위임장 저장 중 오류:', error.message);
    } finally {
      setIsPreparingDocument(false);
    }
  };

  const goPreviewPage = () => {
    sendClickEvent('request_after_autograph_poa-zoom-in');
    router.push({
      pathname: COMMON_PATHS.위임장미리보기,
      query: { type: businessType }
    });
  };

  useEffect(() => {
    if (formValues.isReady && !formValues.autographDataUrl) {
      showDialog({
        title: '서명이 필요해요',
        description: '위임장에 서명이 완료되지 않았어요. 서명을 완료한 후 다시 시도해주세요.',
        buttons: {
          primary: {
            text: '서명하러 가기',
            onClick: () => {
              router.back();
            }
          }
        }
      });
    }
  }, [formValues.autographDataUrl, formValues.isReady, router, showDialog]);

  return (
    <>
      <div className="container-medium container-padding-medium pb-spacious-small w-full flex flex-col flex-1">
        <MainTitle title={'동의가 완료되었어요.'} />
        <div className={'relative rounded-bzc-medium border border-neutral-border-high p-normal-medium'}>
          <PoaPreview documentHtml={documentHtml} isAllowZoom={false} />
          <IconButton
            className={'absolute bottom-normal-medium right-normal-medium !rounded-bzc-xsmall'}
            iconType={'arrow-diagonal-up-right'}
            variant={'filled-secondary'}
            size={'small'}
            onClick={goPreviewPage}
          />
        </div>
      </div>
      <StickyBottomWrapper className={'sticky w-full'}>
        <BoxButton
          size={'large'}
          className={'w-full'}
          onClick={saveDocument}
          disabled={isPreparingDocument || isSaveApplyRefundDelegationFilesInFlight}
          isLoading={isPreparingDocument || isSaveApplyRefundDelegationFilesInFlight}
        >
          다음
        </BoxButton>
      </StickyBottomWrapper>
      <div className="pointer-events-none fixed left-[-99999px] top-0">
        <div ref={captureRef} style={{ width: `${A4_WIDTH}px`, minHeight: `${A4_HEIGHT}px` }} dangerouslySetInnerHTML={{ __html: documentHtml }} />
      </div>
    </>
  );
};
